import { QueryOptions } from '@/api/common/types.ts'
import { QueryMachineryResponse } from '@/api/machinery/response.types.ts'
import { useMachineryApiClient } from '@/api/machinery/useMachineryApiClient.ts'
import { MachineryType, MachineryTypeValue } from '@/domain/models/machineryType.ts'
import { useQueryItems } from '@/presentation/common/hooks/crud/v2/useQueryItems.ts'
import { useCachedPaginatedData } from '@/presentation/common/hooks/useCachedPaginatedData.ts'

type QueryMachineryOptions = Partial<QueryOptions & {
  resetSearch: boolean
}>

export type QueryMachineryForSelectorResult = {
  id: string
  type: MachineryTypeValue
  code: string
  brand: string
  model: string
  serialNumber: string
}

export const useQueryMachineryForSelector = () => {
  const repository = useMachineryApiClient()
  const queryItemsHook = useQueryItems<QueryMachineryResponse>({
    callable : repository.query,
    messages : {
      default: 'No se encontraron maquinarias',
    },
  })

  const {
    mergedData: data,
    lastCachedPage,
    hasMorePages,
    resetCache,
    addPageToCache,
  } = useCachedPaginatedData<QueryMachineryForSelectorResult>()

  const query = async (options: QueryMachineryOptions = {}) => {
    const response = await queryItemsHook.query(options)
    mapResponse(options, response)
  }

  const mapResponse = (options: QueryMachineryOptions, response?: QueryMachineryResponse) => {
    if (!response) {
      return resetCache()
    }

    addPageToCache(response.page ?? 1, {
      ...response,
      items: response.items.map((item) => ({
        id           : item.id.toString(),
        type         : MachineryType.fromString(item.type).value,
        code         : item.code,
        brand        : item.brand,
        model        : item.model,
        serialNumber : item.serialNumber,
      })),
    }, options.resetSearch)
  }

  return {
    ...queryItemsHook,
    query,
    data,
    lastPage: lastCachedPage,
    hasMorePages,
  }
}
